import { html, useEffect, useRef, useState }
  from '/vendor/htm-preact.module.js';
import { get, post } from '/api.js';

/**
 * A game of killer, on the wall.
 *
 * The order down one side with everybody's lives beside them, and the four
 * things that can happen to a shot in the other — big enough to hit from
 * where you are standing, because the person tapping this is holding a cue.
 *
 * The server answers every tap with the whole table, so the screen never has
 * to follow a tap with a fetch. That matters more here than in a match: a
 * killer game is a tap every twenty seconds with five people watching, and a
 * round trip's worth of nothing-happened is what makes somebody tap again.
 *
 * Written small-first like the match screen. A phone is not big enough for
 * this and that is fine — it just has to say so by degrees rather than by
 * putting the buttons on top of each other.
 */

// The four outcomes, in the order the argument at the table goes: did it go
// down, did nothing go down, was it a foul, was it the black.
const SHOTS = [
  { id: 'pot', label: 'Potted', hint: 'Safe', tone: 'felt' },
  { id: 'miss', label: 'Nothing down', hint: 'A life', tone: 'cost' },
  { id: 'foul', label: 'Foul', hint: 'A life', tone: 'cost' },
  // Once it is down it stays down, so this one goes dead for the rest of the
  // rack — one life per rack, and a button that still looked live would be
  // promising a life the rules will not pay.
  { id: 'black', label: 'Black', hint: 'A life back', tone: 'gain' },
];

export function KillerScreen({ killerId, me, kiosk, onDone }) {
  const [game, setGame] = useState(null);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  // Refs, not the state above: the polling effect closes over its variables
  // once and would go on reading the `busy` that was false when the screen
  // mounted — so a refresh landing mid-tap would put the pre-tap table back
  // and take a life somebody had just been given.
  const sending = useRef(0);
  const era = useRef(0);

  const at = `/api/killer/${encodeURIComponent(killerId)}`;

  useEffect(() => {
    let live = true;
    get(at)
      .then((g) => { if (live) setGame(g); })
      .catch((e) => { if (live) setError(e.detail || e.message); });
    // Somebody scoring from a phone at the same table should show here
    // without anyone poking the wall.
    const tick = setInterval(() => {
      if (sending.current) return;
      const mine = era.current;
      get(at)
        .then((g) => {
          // And a read that set off before a tap must not land after it.
          if (live && !sending.current && mine === era.current) setGame(g);
        })
        .catch(() => {});
    }, 8000);
    return () => { live = false; clearInterval(tick); };
  }, [at]);

  const send = async (where, body) => {
    sending.current += 1;
    era.current += 1;
    setBusy(true);
    setError('');
    try {
      setGame(await post(`${at}/${where}`, body || {}));
    } catch (e) {
      setError(e.detail || e.message);
    } finally {
      sending.current -= 1;
      setBusy(false);
    }
  };

  if (error && !game) {
    return html`
      <div class="screen">
        <button class="secondary" onClick=${onDone}>Back</button>
        <p class="error" data-testid="killer-error">${error}</p>
      </div>`;
  }
  if (!game) return html`<p class="sub">Loading…</p>`;

  const players = game.players || [];
  const shots = game.shots || [];

  return html`
    <div class="screen killerscreen"
         data-testid=${kiosk ? 'killer-wall' : 'killer-screen'}>
      <div class="killerhead">
        <h2 data-testid="killer-title">Killer</h2>
        <button class="secondary" style="width:auto"
                data-testid="killer-back" onClick=${onDone}>Back</button>
      </div>

      ${/* Three pieces, placed by the stylesheet rather than by their order
            here: on a wall it is names, then how it stands, then the buttons,
            left to right. On anything smaller they stack, and the buttons go
            above the names — a phone has the width for one column and the
            names are the part you scroll to. */''}

      <div class="killermid">
        ${game.winner
          ? html`
            <div class="killerdone" data-testid="killer-winner">
              <div class="name">Winner</div>
              <div class="figure">${game.winner_name}</div>
            </div>`
          : game.abandoned
            ? html`<p class="empty" data-testid="killer-abandoned">
                     This game was packed up.
                   </p>`
            : html`
              <div class="killerturn" data-testid="killer-turn">
                <div class="name">${game.on_break ? 'To break' : 'At the table'}</div>
                <div class="figure" data-testid="killer-turn-name">
                  ${game.turn_name}
                </div>
                <div class="sub" data-testid="killer-says">
                  ${say(game)}
                </div>
              </div>`}

        <p class="sub" data-testid="killer-balls">
          ${`${game.balls} on the table`}
        </p>
        ${error && html`<p class="error" data-testid="killer-shot-error">
                          ${error}
                        </p>`}
      </div>

      <div class="killerside">
        ${!game.over && html`
          <div class="killerpad" data-testid="killer-pad">
            ${SHOTS.map((shot) => html`
              <button key=${shot.id} class=${shot.tone}
                      disabled=${busy || (shot.id === 'black'
                                          && !game.black_up)}
                      data-testid=${`killer-${shot.id}`}
                      onClick=${() => send('shot', {
                        outcome: shot.id,
                        event_id: crypto.randomUUID(),
                      })}>
                <span class="big">${shot.label}</span>
                <span class="hint">${shot.hint}</span>
              </button>`)}
          </div>`}

        <div class="row">
          <button class="secondary" data-testid="killer-undo"
                  disabled=${busy || shots.length === 0}
                  onClick=${() => send('undo')}>Undo</button>
          ${!game.over && html`
            <button class="secondary" data-testid="killer-abandon"
                    disabled=${busy}
                    onClick=${() => send('abandon')}>Pack up</button>`}
        </div>
      </div>

      <div class="killerorder" data-testid="killer-order">
        ${players.map((p, i) => html`
          <div key=${p.user_id}
               class=${`killerrow${p.out ? ' out' : ''}${
                 p.user_id === game.turn ? ' attable' : ''}`}
               data-testid=${`killer-player-${p.user_id}`}>
            <span class="seat">${i + 1}</span>
            <span class="who" data-testid="killer-name">${p.name}</span>
            <span class="hearts" data-testid=${`killer-lives-${p.user_id}`}
                  aria-label=${`${p.lives} lives`}>
              ${p.out
                ? html`<span class="dead" data-testid="killer-out">Out</span>`
                : Array.from({ length: p.lives }, (_, h) => html`
                    <span class="heart" key=${h}>♥</span>`)}
            </span>
          </div>`)}
      </div>
    </div>`;
}

/**
 * What the person at the table needs to know before they bend down.
 *
 * The break concession is the rule people forget, and finding out about it by
 * losing a life you did not expect to lose is how an argument starts.
 */
function say(game) {
  if (game.retry) return 'Nothing off the break — one more shot, no cost.';
  if (game.on_break) return 'Break. Pot anything and you are safe.';
  if (!game.black_up) {
    return 'Pot anything and you are safe. The black has gone until the rack.';
  }
  return 'Pot anything and you are safe. Nothing down is a life.';
}
